import React, { FC, useContext, useState } from 'react'
import { format, parse } from 'date-fns'
import startCase from 'lodash/startCase'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { useHistory } from 'react-router-dom'
import {
  Badge,
  Box,
  Text,
  Flex,
  Grid,
  GridItem,
  HStack,
  Heading,
  Button,
  Input,
  IconButton,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel
} from '@chakra-ui/react'
import { CheckIcon, PencilSquareIcon, ChevronLeftIcon } from '@heroicons/react/24/outline'
import { capabilities, dispositions, rentals } from '@homevest/utils'

import Evictions from './Evictions'
import Notes from 'components/Notes'
import Ledger from './Ledger'
import UtilityChargebackOverrideAlert from './UtilityChargebackOverrideAlert'
import Tenants from './Tenants'
import Documents from './Documents'
import Cashouts from './Cashouts'
import RentalAgreementHistories from './RentalAgreementHistories'
import MoveInFees from './MoveInFees'
import AddOns from './AddOns'
import Renewals from './Renewals'
import MoveOut from './MoveOut'
import UtilityResponsibilities from './UtilityResponsibilities'
import Buildium from './Buildium'
import RecurringCredits from './RecurringCredits'
import RentalSearch from './RentalSearch'
import { RentalDetailsContext, RentalDetailsContextProvider } from './RentalDetailsContext'
import { getLlc, getRentForRental, getProgramLeaseTypeForRental } from 'lib/rentals'
import { renderRentRollLeaseTypeBadges } from 'lib/rent-roll/lease-types'
import { convertDateToUTC } from 'lib/date-time'
import { hasCapability } from 'lib/admin-perms'
import { StoreState } from 'store'
import { useUpdateTargetMonthlyOptionPremiumMutation } from 'graphql/generated'

import NoteWidgetOption from 'types/NoteWidgetOption'
import { numbers } from '@homevest/utils'
import { Loading } from 'ui'

const { LEDGER_VIEWER } = capabilities.CAPABILITY_TYPES
const { DISPOSITION_STATUSES, DISPOSITION_SALE_TYPES } = dispositions
const { RENTAL_PROGRAM_LEASE_TYPES } = rentals

const RentAndOptionPremiumDisplay: FC<
  React.PropsWithChildren<{
    rentalId: string
    rent: any
    optionPremium: number
    rentalProgramType?: string
    onSuccess?: () => void
  }>
> = ({ rentalId, rent, optionPremium, rentalProgramType, onSuccess }) => {
  const [isEditingOptionPremium, setIsEditingOptionPremium] = useState(false)
  const [optionPremiumInput, setOptionPremiumInput] = useState(optionPremium)
  const [_, updateOptionPremium] = useUpdateTargetMonthlyOptionPremiumMutation()

  const rentLabel = `${numbers.formatMoney(rent, 0, '$')} rent`
  const optionPremiumLabel = `${numbers.formatMoney(optionPremium, 0, '$')} investment`

  const EditButton = (
    <>
      <Text fontSize='2xl'>{`${rentLabel} | ${optionPremiumLabel}`}</Text>
      <Button
        pl={1}
        size='sm'
        variant='unstyled'
        disabled={!!rentalProgramType && rentalProgramType !== RENTAL_PROGRAM_LEASE_TYPES.upandup}
        onClick={() => setIsEditingOptionPremium((x) => !x)}
      >
        <PencilSquareIcon height='1.5em' />
      </Button>
    </>
  )

  const EditOptionPremium = (
    <>
      <Text fontSize='2xl'>{`${rentLabel} | $`}</Text>
      <Input
        w='20'
        type='number'
        value={optionPremiumInput}
        onChange={(e) => setOptionPremiumInput(Number(e.target.value))}
      />
      <Text fontSize='2xl'>investment</Text>
      <IconButton
        aria-label='Set'
        icon={<CheckIcon height={24} width={24} />}
        size='sm'
        colorScheme='teal'
        variant='outline'
        onClick={async () => {
          const result = await updateOptionPremium({
            id: rentalId,
            target_monthly_option_premium: optionPremiumInput * 100
          })
          if (!result.error) {
            onSuccess?.()
          }
          setIsEditingOptionPremium((x) => !x)
        }}
      ></IconButton>
    </>
  )

  return <HStack>{isEditingOptionPremium ? EditOptionPremium : EditButton}</HStack>
}

const TabContent = ({ children }: { children: React.ReactNode }) => (
  <div className='flex flex-col gap-4'>{children}</div>
)

const rentRollTabProps = {
  fontSize: 'sm',
  fontWeight: 'bold',
  px: 3,
  whiteSpace: 'nowrap'
}

function RentalView({ id }: { id: string }) {
  const { rentalData: rental, refreshRentalDetails } = useContext(RentalDetailsContext)
  const [ledgerRefreshKey, setLedgerRefreshKey] = useState(0)
  const history = useHistory()

  const admin = useSelector<StoreState, any>((state) => state.admin)

  const canViewPage = hasCapability(admin, LEDGER_VIEWER)

  if (!canViewPage) {
    history.push('/review')
    return null
  }

  if (!rental) {
    return (
      <div>
        <Button
          leftIcon={<ChevronLeftIcon height={20} />}
          variant='ghost'
          size='md'
          onClick={() => history.push('/rent-roll')}
        >
          Back to Rent Roll
        </Button>
      </div>
    )
  }

  const addressLineOne = rental.portfolio_home?.home?.address?.display_line_1
  const addressLineTwo = rental.portfolio_home?.home?.address?.display_line_2

  const llc = getLlc(rental)

  const rentalProgramType = getProgramLeaseTypeForRental(rental)
  const { optionPremium, rent } = getRentForRental(rental)
  const refreshLedgerPanel = () => setLedgerRefreshKey((key) => key + 1)
  const refreshRentalMonetaryData = refreshRentalDetails
  const refreshCashoutData = () => {
    refreshRentalDetails()
    refreshLedgerPanel()
  }

  const leaseEndReason = rental.lease_end_reason
  const occupancyDate = rental.occupancy_date
  const portfolioHomeDisposition = rental.portfolio_home?.portfolio_home_dispositions.at(0)
  const isNegotiatingCustomerSale =
    portfolioHomeDisposition &&
    portfolioHomeDisposition.sale_type &&
    portfolioHomeDisposition.sale_type !== DISPOSITION_SALE_TYPES.SALE_TO_MARKET &&
    portfolioHomeDisposition.status !== DISPOSITION_STATUSES.SOLD

  const resourceNoteWidgetOptions: NoteWidgetOption[] = []

  const rentalOption: NoteWidgetOption = {
    id: rental.id,
    type: 'rentals',
    writable: true,
    displayName: `Rental for ${addressLineOne}`
  }
  resourceNoteWidgetOptions.push(rentalOption)

  const collectionsOptions: NoteWidgetOption[] = rental.collections.map((c) => ({
    id: c.id,
    type: 'collections',
    writable: false,
    displayName: `Collection for ${format(parse(c.month, 'yyyy-MM-dd', new Date()), 'MMM yyyy')}`
  }))
  resourceNoteWidgetOptions.push(...collectionsOptions)

  return (
    <div>
      <Helmet>
        <title>{`Rental | ${addressLineOne ?? ''}`}</title>
      </Helmet>

      <div className='mt-8'>
        <Flex
          align='flex-start'
          direction={{ base: 'column', lg: 'row' }}
          gap={6}
          justify='space-between'
          mb={6}
        >
          <Flex direction='column' flex='1 1 auto' minW={0}>
            <Text fontSize={'3xl'}>
              <a href={'/home/' + rental.property.id} target='_blank'>
                {addressLineOne}
                <br />
              </a>
              {addressLineTwo}
            </Text>
            <Text fontSize='2xl'>{llc?.name || 'Need to backfill llc_property'}</Text>
            <RentAndOptionPremiumDisplay
              rentalId={rental.id}
              rent={rent}
              optionPremium={optionPremium}
              rentalProgramType={rentalProgramType}
              onSuccess={refreshRentalMonetaryData}
            />
            <Flex direction='column' gap={1} my={3}>
              <HStack>
                {leaseEndReason && (
                  <Badge colorScheme='red' fontSize='xl' variant='outline'>
                    {startCase(leaseEndReason)}
                  </Badge>
                )}
                {isNegotiatingCustomerSale && (
                  <Badge colorScheme='green' fontSize='xl' variant='outline'>
                    Negotiating Customer Purchase
                  </Badge>
                )}
                {renderRentRollLeaseTypeBadges(rental, {
                  dot: true,
                  size: 'lg'
                })}
              </HStack>
              <Evictions rental={rental} />
              <Text fontSize='xl'>Occupancy date: {convertAndDisplayDate(occupancyDate)}</Text>
            </Flex>
          </Flex>
          <Box flex='0 0 auto' maxW='100%' w={{ base: '100%', lg: '24rem' }}>
            <RentalSearch />
          </Box>
        </Flex>

        <Grid
          alignItems='start'
          gap={6}
          templateColumns='minmax(0, 1fr)'
          sx={{
            '@media screen and (min-width: 1280px)': {
              columnGap: 5,
              gridTemplateColumns: 'minmax(28rem, 3fr) minmax(0, 4fr)'
            }
          }}
          w='full'
        >
          <GridItem
            minW={0}
            sx={{
              gridColumn: '1',
              gridRow: '2',
              '@media screen and (min-width: 1280px)': {
                gridColumn: '1',
                gridRow: '1'
              }
            }}
          >
            <Tabs colorScheme='teal' variant='enclosed-colored' isFitted isLazy size='md'>
              <TabList mb={4} overflow='hidden'>
                <Tab {...rentRollTabProps}>Lease Details</Tab>
                <Tab {...rentRollTabProps}>Finances</Tab>
                <Tab {...rentRollTabProps}>Notes</Tab>
              </TabList>

              <TabPanels>
                {/* since we're using isLazy, place each tab in its own Suspense boundary
                 so that whole page doesn't fall back to loading when changing tabs */}
                <React.Suspense fallback={<Loading />}>
                  <TabPanel px={0}>
                    <TabContent>
                      <Tenants rental={rental} onRefresh={refreshRentalDetails} />
                      <RentalAgreementHistories
                        rental={rental}
                        onSuccess={refreshRentalMonetaryData}
                      />
                      <Documents rentalId={id} />
                      {!rental.cashout?.id && (
                        <Renewals rental={rental} onLedgerRefresh={refreshLedgerPanel} />
                      )}
                      <MoveOut
                        rental={rental}
                        onLedgerRefresh={refreshLedgerPanel}
                        onSuccess={refreshRentalDetails}
                      />
                    </TabContent>
                  </TabPanel>
                </React.Suspense>

                <React.Suspense fallback={<Loading />}>
                  <TabPanel px={0}>
                    <TabContent>
                      {rental.cashout?.id && (
                        <Cashouts rental={rental} onSuccess={refreshCashoutData} />
                      )}
                      <Buildium
                        key={rental.id}
                        rentalId={rental.id}
                        buildiumLeaseId={rental.buildium_lease_id}
                      />
                      {!rental.cashout?.id && (
                        <MoveInFees rentalId={id} onMonetaryDataChange={refreshRentalDetails} />
                      )}
                      {!rental.cashout?.id && <RecurringCredits rentalId={id} />}
                      {!rental.cashout?.id && (
                        <AddOns rentalId={id} onMonetaryDataChange={refreshRentalDetails} />
                      )}
                      <UtilityResponsibilities rentalId={id} />
                    </TabContent>
                  </TabPanel>
                </React.Suspense>

                <React.Suspense fallback={<Loading />}>
                  <TabPanel px={0}>
                    <TabContent>
                      <Notes resourceNoteWidgetOptions={resourceNoteWidgetOptions} />
                    </TabContent>
                  </TabPanel>
                </React.Suspense>
              </TabPanels>
            </Tabs>
          </GridItem>

          <GridItem
            minW={0}
            sx={{
              gridColumn: '1',
              gridRow: '1',
              '@media screen and (min-width: 1280px)': {
                gridColumn: '2',
                gridRow: '1'
              }
            }}
          >
            <Flex align='center' borderBottom='1px solid' borderColor='gray.200' h='42px' mb={4}>
              <Heading fontSize='md'>Ledger</Heading>
            </Flex>
            <UtilityChargebackOverrideAlert override={rental.utility_chargeback_override} />
            <React.Suspense fallback={<Loading />}>
              <Ledger
                key={`${id}-${ledgerRefreshKey}`}
                rentalId={id}
                requestPolicy={ledgerRefreshKey > 0 ? 'network-only' : 'cache-and-network'}
                onMonetaryDataChange={refreshRentalDetails}
              />
            </React.Suspense>
          </GridItem>
        </Grid>
      </div>
    </div>
  )
}

function RentalDetails() {
  const { rentalId } = useParams<{ rentalId: string }>()
  return (
    <div style={{ margin: '0 1rem' }}>
      <React.Suspense fallback={<Loading />}>
        <RentalDetailsContextProvider rentalId={rentalId}>
          <RentalView id={rentalId} />
        </RentalDetailsContextProvider>
      </React.Suspense>
    </div>
  )
}

function convertAndDisplayDate(dateObject: any): string {
  return format(convertDateToUTC(dateObject), 'MMMM dd, yyyy')
}

export default RentalDetails
